import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';
import {
  confirmOutreachSourceForJobStatus,
  isConfirmOutreachLanding,
  setConfirmCandidateSource,
} from '~/domain/ConfirmCandidateSource';

interface IUseConfirmOutreachSourceArgs {
  /** Shift the confirm screen is showing. Attribution waits for it to load. */
  jobId?: string;
  /** `pending` or `counter` — decides the applied/counter split. */
  jobStatus?: string;
}

/**
 * Attributes a confirmation-outreach email landing (GOTU-4392) so a later confirm
 * on this screen reports it as its source (GOTU-4906).
 *
 * Lives here rather than with the other entry points because this link is the one
 * that lands *on* the confirm screen instead of navigating to it — there is no
 * earlier click to hang the attribution on. It also can't be resolved from the URL
 * alone: the plain and counter variants share one `utm_medium` and differ only in
 * which shift they point at, so this waits for the shift to load and reads its
 * status.
 */
export const useConfirmOutreachSource = ({
  jobId,
  jobStatus,
}: IUseConfirmOutreachSourceArgs): void => {
  const [searchParams] = useSearchParams();
  // Captured on first render: the confirm screen replaces the URL once the
  // candidate list settles, which drops the query string before the shift loads.
  const isOutreachLandingRef = useRef(isConfirmOutreachLanding(searchParams));
  const hasRecordedRef = useRef(false);

  useEffect(() => {
    if (!isOutreachLandingRef.current || hasRecordedRef.current || !jobId) {
      return;
    }

    const source = confirmOutreachSourceForJobStatus(jobStatus);
    if (!source) {
      return;
    }

    hasRecordedRef.current = true;
    setConfirmCandidateSource({ source, shiftId: jobId });
  }, [jobId, jobStatus]);
};
